import { z } from 'zod';
import { moneyMinorSchema } from '../api';
import { SEX } from '../enums';
// ── Labour and delivery (SRS 6.8) ────────────────────────────────────────────
export const DELIVERY_MODE = ['normal', 'assisted', 'caesarean', 'vacuum', 'forceps'];
export const DELIVERY_OUTCOME = ['live_birth', 'still_birth', 'neonatal_death', 'abortion'];
export const openDeliverySchema = z.object({
    patientId: z.string().uuid(),
    admissionId: z.string().uuid().nullable().optional(),
    gravida: z.number().int().min(1).max(30).optional(),
    para: z.number().int().min(0).max(30).optional(),
    gestationWeeks: z.number().min(20).max(45).optional(),
    onsetAt: z.string().datetime().optional(),
    notes: z.string().max(4000).optional(),
});
/** One partograph observation (SRS 6.8). */
export const partographEntrySchema = z.object({
    at: z.string().datetime().optional(),
    cervixCm: z.number().min(0).max(10).optional(),
    descentFifths: z.number().int().min(0).max(5).optional(),
    contractionsPer10Min: z.number().int().min(0).max(10).optional(),
    contractionSeconds: z.number().int().min(0).max(180).optional(),
    foetalHeartRate: z.number().int().min(40).max(240).optional(),
    liquor: z.enum(['intact', 'clear', 'meconium', 'blood_stained', 'absent']).optional(),
    maternalPulse: z.number().int().min(30).max(220).optional(),
    bpSystolic: z.number().int().min(50).max(260).optional(),
    bpDiastolic: z.number().int().min(30).max(180).optional(),
    temperatureC: z.number().min(30).max(43).optional(),
    notes: z.string().max(600).optional(),
});
export const completeDeliverySchema = z.object({
    deliveredAt: z.string().datetime().optional(),
    mode: z.enum(DELIVERY_MODE),
    outcome: z.enum(DELIVERY_OUTCOME).default('live_birth'),
    anaesthesia: z.string().trim().max(80).optional(),
    episiotomy: z.boolean().default(false),
    bloodLossMl: z.number().int().min(0).max(10_000).optional(),
    complications: z.string().max(2000).optional(),
    notes: z.string().max(8000).optional(),
    newborns: z
        .array(z.object({
        sex: z.enum(SEX).default('unknown'),
        birthWeightG: z.number().int().min(200).max(8000).optional(),
        lengthCm: z.number().min(15).max(70).optional(),
        headCircumCm: z.number().min(15).max(60).optional(),
        /** APGAR at one, five and ten minutes (SRS 6.8). */
        apgar1: z.number().int().min(0).max(10).optional(),
        apgar5: z.number().int().min(0).max(10).optional(),
        apgar10: z.number().int().min(0).max(10).optional(),
        resuscitated: z.boolean().default(false),
        bcgGiven: z.boolean().default(false),
        opvGiven: z.boolean().default(false),
        hepBGiven: z.boolean().default(false),
        notes: z.string().max(1000).optional(),
    }))
        .max(6)
        .default([]),
});
// ── Dialysis, day care and physiotherapy (SRS 6.8) ───────────────────────────
export const THERAPY_UNIT = ['dialysis', 'chemotherapy', 'physiotherapy', 'day_care'];
export const THERAPY_SESSION_STATUS = ['scheduled', 'in_progress', 'completed', 'cancelled', 'no_show'];
export const upsertStationSchema = z.object({
    unit: z.enum(THERAPY_UNIT),
    code: z.string().trim().toUpperCase().min(1).max(16),
    name: z.string().trim().min(2).max(120),
    branchId: z.string().uuid().nullable().optional(),
    isActive: z.boolean().default(true),
});
export const scheduleSessionSchema = z.object({
    patientId: z.string().uuid(),
    admissionId: z.string().uuid().nullable().optional(),
    unit: z.enum(THERAPY_UNIT),
    stationId: z.string().uuid().nullable().optional(),
    /** Cycle number within a course, e.g. chemotherapy cycle 3 of 6. */
    cycleNo: z.number().int().min(1).max(200).default(1),
    protocol: z.string().trim().max(200).optional(),
    scheduledAt: z.string().datetime(),
    durationMinutes: z.number().int().min(15).max(1440).default(240),
    chargeItemId: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).optional(),
});
export const startSessionSchema = z.object({
    stationId: z.string().uuid().nullable().optional(),
    preWeightKg: z.number().min(0.5).max(400).optional(),
    chart: z.record(z.string(), z.unknown()).optional(),
});
export const completeSessionSchema = z.object({
    postWeightKg: z.number().min(0.5).max(400).optional(),
    chart: z.record(z.string(), z.unknown()).optional(),
    complications: z.string().max(2000).optional(),
    /** Consumables opened during the session; they post to the patient's bill. */
    consumables: z
        .array(z.object({
        productId: z.string().uuid(),
        qty: z.number().min(0.001),
    }))
        .max(50)
        .default([]),
    /** Raise the session charge on an invoice rather than leaving it unbilled. */
    bill: z.boolean().default(true),
    notes: z.string().max(4000).optional(),
});
// ── Dental (SRS 6.8) ─────────────────────────────────────────────────────────
export const DENTAL_STATUS = ['planned', 'in_progress', 'completed', 'cancelled'];
/** FDI notation: quadrant 1–4 permanent, 5–8 deciduous, tooth 1–8. */
const toothSchema = z.string().regex(/^[1-8][1-8]$/, 'Expected FDI tooth notation, e.g. 36');
export const dentalTreatmentSchema = z.object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid().nullable().optional(),
    toothNo: toothSchema,
    surfaces: z.array(z.enum(['mesial', 'distal', 'occlusal', 'buccal', 'lingual', 'incisal', 'cervical'])).max(7).default([]),
    finding: z.string().trim().min(2).max(200),
    procedure: z.string().trim().max(200).optional(),
    chargeItemId: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).optional(),
});
export const dentalUpdateSchema = z.object({
    status: z.enum(DENTAL_STATUS),
    procedure: z.string().trim().max(200).optional(),
    notes: z.string().max(2000).optional(),
    /** Bills the completed procedure to the patient. */
    bill: z.boolean().default(false),
    priceMinor: moneyMinorSchema.min(0).optional(),
});
//# sourceMappingURL=specialty.js.map